import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAddMemberCollapsibleStore } from './useAddMemberCollapsibleStore';
import { createCampaignMember } from 'api/campaigns';
import { uploadFile } from 'api/media';
import * as AddMemberCollapsible from 'assets/js/pages/campaign-settings/campaign-members/AddMemberCollapsible';
import * as MemberManualForm from 'assets/js/pages/campaign-settings/campaign-members/MemberFormInputs';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Form from 'components/form';
import { UploadFile } from 'components/upload';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useForceRender } from 'customHooks/useForceRender';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { GivebutterAxiosError } from 'types/error';

const AddMemberManualFormCollapsible = () => {
    const toast = useToast();
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { collapsibleCard, setCollapsibleCard } = useAddMemberCollapsibleStore();
    const isCollapsibleOpen = collapsibleCard === 'manual';
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id!;
    const [shouldCreateAnother, setShouldCreateAnother] = useState(false);
    const methods = Form.useForm<MemberManualForm.MemberManualFormValues>({
        defaultValues: {
            displayName: '',
            goal: '',
            team: [],
            picture: [],
        },
    });
    const { key, forceRender } = useForceRender();
    const { reset, handleSubmit } = methods;

    const { isPending: isSubmitting, mutate: submit } = useMutation({
        mutationFn: async (values: MemberManualForm.MemberManualFormValues) => {
            const nextValues = await MemberManualForm.transformFormValues(values);
            const picture = methods.getValues('picture')[0] as UploadFile | undefined;

            let nextPicture: string | null = null;
            try {
                if (picture) {
                    const { data } = await uploadFile(picture.file);
                    nextPicture = data.url;
                }
            } catch (e) {}

            return createCampaignMember(campaignId, {
                ...nextValues,
                picture: nextPicture,
            });
        },
        onSuccess: () => {
            toast.success('Added team member successfully!');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaignId),
                type: 'all',
            });

            if (shouldCreateAnother) {
                reset();
                forceRender();
            } else {
                goBack();
            }
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to add team member');
        },
    });

    return (
        <Form.Provider {...methods}>
            <AddMemberCollapsible.Root
                open={isCollapsibleOpen}
                onOpenChange={() => setCollapsibleCard('manual')}
                className={'group-radix-state-closed:gap-0'}
            >
                <AddMemberCollapsible.Trigger>
                    <AddMemberCollapsible.TriggerLeft>
                        <FontAwesomeIcon
                            size={'lg'}
                            className={'text-blue-500'}
                            icon={faPlusCircle}
                        />
                    </AddMemberCollapsible.TriggerLeft>
                    <AddMemberCollapsible.TriggerRight>
                        <div className={'flex items-center justify-between'}>
                            <p className={'text-sm font-medium'}>Add manually</p>
                            <AddMemberCollapsible.ExpandIcon />
                        </div>
                    </AddMemberCollapsible.TriggerRight>
                </AddMemberCollapsible.Trigger>
                <AddMemberCollapsible.Content
                    forceMount={true}
                    className={
                        'group-radix-state-closed:invisible group-radix-state-closed:absolute group-radix-state-closed:h-0 group-radix-state-open:visible'
                    }
                >
                    <form
                        className={'contents'}
                        onSubmit={handleSubmit(
                            submit as SubmitHandler<MemberManualForm.MemberManualFormValues>,
                        )}
                    >
                        <div className={'flex flex-col gap-8'}>
                            <div className={'flex flex-col gap-4'}>
                                <p className={'text-sm'}>
                                    Please note that adding a team member manually will prevent them
                                    from having access to and updating their pages.
                                </p>
                                <div className={'flex flex-wrap gap-4'}>
                                    <MemberManualForm.Picture key={key} />
                                    <div className={'flex flex-grow flex-col gap-4'}>
                                        <MemberManualForm.DisplayName />
                                        <MemberManualForm.Goal />
                                        <MemberManualForm.Team />
                                    </div>
                                </div>
                            </div>
                            <div className={'flex w-full flex-wrap gap-4'}>
                                <Form.SubmitButton
                                    size={'sm'}
                                    isSubmitting={isSubmitting}
                                    className={'w-full sm:w-auto sm:flex-1'}
                                    onClick={() => setShouldCreateAnother(true)}
                                >
                                    Create and add another
                                </Form.SubmitButton>
                                <Form.SubmitButton
                                    size={'sm'}
                                    noAnimation={true}
                                    isSubmitting={isSubmitting}
                                    className={'w-full sm:w-auto sm:flex-1'}
                                    onClick={() => setShouldCreateAnother(false)}
                                    variant={'outline'}
                                    color={'neutral'}
                                >
                                    Create and close
                                </Form.SubmitButton>
                            </div>
                        </div>
                    </form>
                </AddMemberCollapsible.Content>
            </AddMemberCollapsible.Root>
        </Form.Provider>
    );
};

export { AddMemberManualFormCollapsible };
